import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Ship, Truck, Plane, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { Badge } from '../../components/ui/Badge';
import { FilterBar } from '../../components/ui/FilterBar';
import api from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { PER_PAGE, STATUS_KEYS, fmtDate } from './utils';
import { TransportCell }      from './components/TransportCell';
import { FilterModal }        from './components/FilterModal';
import { ShipmentEmptyState } from './components/ShipmentEmptyState';
import { ShipmentDrawer }     from './components/ShipmentDrawer';

export default function ShipmentsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !hasPermission('view_shipments')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, hasPermission, navigate]);

  const [shipments, setShipments]   = useState([]);
  const [total, setTotal]           = useState(0);
  const [counts, setCounts]         = useState({ all: 0, transit: 0, delivered: 0, incident: 0 });
  const [orders, setOrders]         = useState([]);
  const [companies, setCompanies]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [filter, setFilter]         = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [search, setSearch]         = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage]             = useState(1);
  const [selectedRow, setSelectedRow]   = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({ statut: [], origin: '', destination: '' });

  const loadMeta = useCallback(() => {
    const isAdmin = user?.company?.type === 'ADMIN';
    const requests = [
      api.get('/orders'),
      ...(isAdmin ? [api.post('/companies/get-all?skip=0&limit=500', { type: 'CLIENT' })] : []),
    ];
    Promise.all(requests).then(([oRes, cRes]) => {
      setOrders(oRes.data);
      if (cRes) {
        const list = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.companies || []);
        setCompanies(list);
      }
    });
  }, [user]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const loadShipments = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
    if (filter !== 'all')                        params.set('statusGroup', filter);
    if (modeFilter !== 'all')                    params.set('mode', modeFilter);
    if (debouncedSearch.trim())                  params.set('q', debouncedSearch.trim());
    if (advancedFilters.origin.trim())           params.set('origin', advancedFilters.origin.trim());
    if (advancedFilters.destination.trim())      params.set('destination', advancedFilters.destination.trim());
    advancedFilters.statut.forEach((s) =>        params.append('status', s));
    api.get(`/shipments?${params}`)
      .then((res) => {
        setShipments(res.data.data ?? []);
        setTotal(res.data.total ?? 0);
        if (res.data.counts) setCounts(res.data.counts);
      })
      .catch((err) => setError(err.message || t('common.error_occurred')))
      .finally(() => setLoading(false));
  }, [filter, modeFilter, debouncedSearch, advancedFilters, page, t]);

  useEffect(() => { loadShipments(); }, [loadShipments]);

  function refresh() { loadMeta(); loadShipments(); }

  const enriched = useMemo(() => {
    const orderMap   = Object.fromEntries(orders.map((o) => [o._id, o]));
    const companyMap = Object.fromEntries(companies.map((c) => [String(c._id), c.name]));

    return shipments.map((s) => {
      const order  = orderMap[s.orderId] || {};
      const mapped = STATUS_KEYS[s.status] || { variant: 'info', key: null, group: 'other' };
      return {
        _id:                s._id,
        containerRef:       s.containerRef,
        clientName:         companyMap[String(s.clientId)] || user?.company?.name || '—',
        productName:        order.productName || '—',
        departure:          order.origin?.city || order.origin?.portCode || '—',
        arrival:            order.destination?.finalDestinationCode || order.destination?.portCode || '—',
        originCity:         order.origin?.city,
        originCountryCode:  order.origin?.countryCode,
        etd:                fmtDate(order.etd, locale),
        quantityTons:       s.quantityTons,
        mode:               s.mode || order.mode,
        statusVariant:      mapped.variant,
        statusKey:          mapped.key,
        statusGroup:        mapped.group,
        status:             s.status,
        containerType:      s.containerType,
        temperatureSetPoint: s.temperatureSetPoint,
        sealNumber:         s.sealNumber,
        incident:           s.incident,
        documents:          s.documents,
        orderRef:           order.reference,
        atd:                fmtDate(order.atd, locale),
        shipName:           order.shipName,
        freightVendorName:  order.freightVendorName,
        incoterm:           order.incoterm,
        linerTermCode:      order.linerTermCode,
        weekNo:             order.weekNo,
        totalQuantityTons:  order.totalQuantityTons,
        createdAt:          s.createdAt,
        updatedAt:          s.updatedAt,
      };
    });
  }, [shipments, orders, companies, user, locale]);

  const columns = useMemo(() => [
    {
      key: 'containerRef',
      label: t('shipments.col_ref'),
      render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
    },
    {
      key: 'clientName',
      label: t('shipments.col_client'),
      render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
    },
    {
      key: 'productName',
      label: t('shipments.col_product'),
      render: (v) => <span className="font-medium text-slate-700">{v}</span>,
    },
    {
      key: 'departure',
      label: t('shipments.col_route'),
      render: (_, row) => (
        <span className="text-slate-500 text-[12px]">{row.departure} → {row.arrival}</span>
      ),
    },
    {
      key: 'etd',
      label: t('shipments.col_etd'),
      render: (v) => <span className="text-slate-600">{v}</span>,
    },
    {
      key: 'quantityTons',
      label: t('shipments.col_qty'),
      align: 'right',
      render: (v) => <span className="tabular-nums text-slate-700 font-medium">{v != null ? v : '—'}</span>,
    },
    {
      key: 'mode',
      label: t('shipments.col_mode'),
      render: (v) => <TransportCell mode={v} />,
    },
    {
      key: 'statusVariant',
      label: t('shipments.col_status'),
      render: (v, row) => (
        <Badge variant={v}>{row.statusKey ? t(`shipments.${row.statusKey}`) : '—'}</Badge>
      ),
    },
    ...syncTimestampColumns(t, locale),
  ], [t, locale]);

  const activeFilterCount =
    (advancedFilters.origin.trim() ? 1 : 0) +
    (advancedFilters.destination.trim() ? 1 : 0) +
    advancedFilters.statut.length;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('shipments.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {t('shipments.count_other', { count: counts.all })}
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
        >
          <RefreshCw size={13} strokeWidth={1.75} className={loading ? 'animate-spin' : ''} />
          {t('common.refresh')}
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
          <span>{error}</span>
          <button onClick={refresh} className="font-medium underline text-[12px] text-rose-700">
            {t('common.retry')}
          </button>
        </div>
      )}

      <FilterBar
        groups={[
          {
            value: filter,
            onChange: (key) => { setFilter(key); setPage(1); },
            tabs: [
              { key: 'all',       label: t('shipments.filter_all'),       count: counts.all       },
              { key: 'transit',   label: t('shipments.filter_transit'),   count: counts.transit   },
              { key: 'delivered', label: t('shipments.filter_delivered'), count: counts.delivered },
              { key: 'incident',  label: t('shipments.filter_incident'),  count: counts.incident  },
            ],
          },
          {
            value: modeFilter,
            onChange: (key) => { setModeFilter(key); setPage(1); },
            tabs: [
              { key: 'all',    label: t('common.all'), Icon: null  },
              { key: 'reefer', label: 'Reefer',        Icon: Ship  },
              { key: 'road',   label: 'Route',         Icon: Truck },
              { key: 'air',    label: 'Air',           Icon: Plane },
            ],
          },
        ]}
        search={search}
        onSearch={(e) => { setSearch(e.target.value); setPage(1); }}
        searchPlaceholder={t('shipments.search_placeholder')}
        actions={
          <button
            onClick={() => setShowFilterModal(true)}
            className={`relative h-9 px-3 flex items-center gap-1.5 rounded-lg border text-[12px] font-medium transition ${
              activeFilterCount > 0
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={13} strokeWidth={1.75} />
            {t('shipments.filter_btn')}
            {activeFilterCount > 0 && (
              <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        }
      />

      <FilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        values={advancedFilters}
        onApply={(draft) => { setAdvancedFilters(draft); setPage(1); }}
        onReset={() => { setAdvancedFilters({ statut: [], origin: '', destination: '' }); setPage(1); }}
      />

      <Table
        columns={columns}
        rows={enriched}
        onRowClick={setSelectedRow}
        loading={loading}
        emptyState={<ShipmentEmptyState />}
        page={page}
        total={total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <ShipmentDrawer row={selectedRow} onClose={() => setSelectedRow(null)} companyName={user?.company?.name} />
    </div>
  );
}
